/**
 * Recently viewed & related — product page unit 11 (.mockup/product.html:973-1030).
 *
 * Liquid cannot read localStorage, so the rail is filled here. Nothing about the store is
 * reimplemented: the ids come from Horizon's own `@theme/recently-viewed-products`, which
 * `snippets/scripts.liquid:277` already writes on every product page, and the cards come from
 * the Section Rendering API against the search URL — the `id:X OR id:Y` route
 * `snippets/search-modal.liquid:45` already uses for the same store.
 *
 * Two things worth knowing about the fetch:
 *
 * 1. The section is requested by **filename**, not by its `templates/product.json` key, because
 *    the Section Rendering API can only reach a template's own sections from that template's
 *    URL and `search` is only populated on the search route. `sections/predictive-search.liquid`
 *    is fetched the same way. That render has no template instance, so it returns the rail
 *    alone and only `[data-recently-viewed-rail]` is read out of it.
 *
 * 2. The current product is dropped before the query is built. Horizon's store caps at four ids
 *    and always holds the product being viewed, so what is left is the mockup's three cards.
 *
 * There is no IntersectionObserver here, unlike `assets/product-recommendations.js`: the section
 * starts hidden, and a `display: none` element never intersects.
 */

import { Component } from '@theme/component';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { sectionRenderer } from '@theme/section-renderer';

class RecentlyViewedComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#loadRail();
  }

  /**
   * Fills the rail from the recently viewed store and reveals the section.
   */
  async #loadRail() {
    const { sectionId, productId } = this.dataset;
    const { rail } = this.refs;

    if (!sectionId || !rail) return;

    const ids = RecentlyViewed.getProducts().filter((/** @type {string} */ id) => id !== productId);
    if (ids.length === 0) return;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', ids.map((/** @type {string} */ id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    try {
      const html = await sectionRenderer.getSectionHTML(sectionId, false, url);
      const cards = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('[data-recently-viewed-rail]');

      if (!cards?.children.length) return;

      rail.replaceChildren(...cards.children);
      this.hidden = false;
    } catch (error) {
      // A failed fetch leaves the section hidden, which is the same outcome as no history.
      console.error('Recently viewed products error:', error instanceof Error ? error.message : error);
    }
  }
}

if (!customElements.get('recently-viewed-component')) {
  customElements.define('recently-viewed-component', RecentlyViewedComponent);
}
